import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, FileText, Search, Calendar as CalendarIcon, X } from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";
import { api, formatINR, updateQuotationStatus, QUOTE_STATUSES, QUOTE_STATUS_META, normalizeStatus } from "@/lib/quotationApi";
import HistoryCard from "@/components/history/HistoryCard";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { useAuth } from "@/lib/auth";
import AppHeader from "@/components/AppHeader";

const ACTION_FILTERS = [
  { value: "all", label: "All actions" },
  { value: "created", label: "Created" },
  { value: "edited", label: "Edited" },
  { value: "sent", label: "Marked Sent" },
  { value: "approved", label: "Approved" },
  { value: "reopened", label: "Approval removed" },
  { value: "duplicated", label: "Duplicated" },
];

// Module-scope predicate keeps the filtering logic out of the component and
// the useMemo below small.
const quoteMatchesFilters = (it, { q, range, statusFilter, isManagerUp, activityAction, activityActor }) => {
  if (q) {
    const hay = [
      it.customer?.name,
      it.customer?.mobile,
      it.customer?.city,
      it.customer?.location,
      it.customer?.quotation_given_by,
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();
    if (!hay.includes(q)) return false;
  }
  if (range?.from) {
    const start = new Date(range.from);
    start.setHours(0, 0, 0, 0);
    if (new Date(it.created_at) < start) return false;
  }
  if (range?.to) {
    const end = new Date(range.to);
    end.setHours(23, 59, 59, 999);
    if (new Date(it.created_at) > end) return false;
  }
  if (statusFilter !== "all" && normalizeStatus(it.status) !== statusFilter) {
    return false;
  }
  if (isManagerUp && (activityAction !== "all" || activityActor !== "all")) {
    const acts = it.activity || [];
    const match = acts.some(
      (a) =>
        (activityAction === "all" || a.action === activityAction) &&
        (activityActor === "all" || a.by_user_id === activityActor),
    );
    if (!match) return false;
  }
  return true;
};

export default function History() {
  const { can, role } = useAuth();
  const isManagerUp = role === "admin" || role === "manager";
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [range, setRange] = useState(undefined);
  const [dateOpen, setDateOpen] = useState(false);
  const [duplicatingId, setDuplicatingId] = useState(null);
  const [activityFor, setActivityFor] = useState(null);
  const [activityAction, setActivityAction] = useState("all");
  const [activityActor, setActivityActor] = useState("all");
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/quotations");
      setItems(r.data);
    } catch {
      toast.error("Failed to load");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    const opts = {
      q: query.trim().toLowerCase(),
      range,
      statusFilter,
      isManagerUp,
      activityAction,
      activityActor,
    };
    return items.filter((it) => quoteMatchesFilters(it, opts));
  }, [items, query, statusFilter, range, isManagerUp, activityAction, activityActor]);

  const actors = useMemo(() => {
    const m = new Map();
    items.forEach((it) =>
      (it.activity || []).forEach((a) => {
        if (a.by_user_id && !m.has(a.by_user_id)) m.set(a.by_user_id, a.by_name || a.by_user_id);
      }),
    );
    return Array.from(m, ([id, name]) => ({ id, name }));
  }, [items]);

  const onDelete = async (id) => {
    if (!window.confirm("Delete this quotation?")) return;
    try {
      await api.delete(`/quotations/${id}`);
      toast.success("Deleted");
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  const onStatusChange = async (id, status) => {
    const prev = items;
    setItems((cur) => cur.map((it) => (it.id === id ? { ...it, status } : it)));
    try {
      await updateQuotationStatus(id, status);
      toast.success(`Marked ${QUOTE_STATUS_META[status].label}`);
    } catch {
      toast.error("Status update failed");
      setItems(prev);
    }
  };

  const onDuplicate = async (id) => {
    if (
      !window.confirm(
        "Duplicate this quote with today's master prices?\n\nThe original stays untouched. The new copy will use current finishes, accessories, tier discounts and modular multiplier.",
      )
    )
      return;
    setDuplicatingId(id);
    try {
      const r = await api.post(`/quotations/${id}/duplicate`);
      toast.success("Duplicated with current prices");
      navigate(`/quotations/${r.data.id}`);
    } catch {
      toast.error("Duplicate failed");
    } finally {
      setDuplicatingId(null);
    }
  };

  const totalValue = filtered.reduce((s, q) => s + (q.grand_total || 0), 0);

  return (
    <>
    <AppHeader />
    <div className="min-h-screen pt-24 pb-16 max-w-2xl mx-auto px-4">
      <Link to="/dashboard" className="inline-flex items-center gap-1.5 text-white/50 hover:text-white text-sm mb-6" data-testid="history-back">
        <ArrowLeft size={14} /> Dashboard
      </Link>

      <div className="mb-6">
        <div className="overline text-[#C5A880] mb-2">History</div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold">Saved quotations</h1>
        <p className="text-white/50 mt-2 text-sm">
          {filtered.length} of {items.length} quotation{items.length !== 1 ? "s" : ""}
          {totalValue > 0 && (
            <> · Total <span className="text-[#C5A880] font-display">{formatINR(totalValue)}</span></>
          )}
        </p>
      </div>

      {/* Search + filters */}
      <div className="card-luxe mb-4">
        <div className="relative">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
          <input
            className="input-luxe !pl-10"
            placeholder="Search by name, mobile, city..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            data-testid="history-search"
          />
        </div>
        <div className="mt-3">
          <label className="label-luxe flex items-center gap-1.5 mb-1.5">
            <CalendarIcon size={10} /> Date range
          </label>
          <Popover open={dateOpen} onOpenChange={setDateOpen}>
            <PopoverTrigger asChild>
              <button
                type="button"
                className="input-luxe !h-12 flex items-center justify-between text-left w-full"
                data-testid="history-date-trigger"
              >
                <span className={range?.from ? "text-white" : "text-white/40"}>
                  {range?.from
                    ? range.to
                      ? `${format(range.from, "d MMM yyyy")} — ${format(range.to, "d MMM yyyy")}`
                      : `${format(range.from, "d MMM yyyy")} — …`
                    : "Pick a date range"}
                </span>
                {range?.from ? (
                  <X
                    size={15}
                    className="text-white/40 hover:text-white shrink-0"
                    data-testid="history-date-clear"
                    aria-label="Clear date range"
                    onClick={(e) => {
                      e.stopPropagation();
                      setRange(undefined);
                    }}
                  />
                ) : (
                  <CalendarIcon size={15} className="text-white/40 shrink-0" />
                )}
              </button>
            </PopoverTrigger>
            <PopoverContent
              className="w-auto p-0 bg-[#12151b] border-white/10"
              align="start"
              data-testid="history-date-popover"
            >
              <Calendar
                mode="range"
                selected={range}
                onSelect={setRange}
                numberOfMonths={1}
                initialFocus
              />
            </PopoverContent>
          </Popover>
        </div>
        {(query || range?.from || activityAction !== "all" || activityActor !== "all") && (
          <button
            onClick={() => {
              setQuery("");
              setRange(undefined);
              setActivityAction("all");
              setActivityActor("all");
            }}
            className="text-xs text-[#C5A880] hover:underline mt-3"
            data-testid="history-clear-filters"
          >
            Clear filters
          </button>
        )}

        {/* Status filter */}
        <div className="flex flex-wrap gap-2 mt-4" data-testid="history-status-filter">
          {["all", ...QUOTE_STATUSES].map((s) => {
            const active = statusFilter === s;
            const label = s === "all" ? "All" : QUOTE_STATUS_META[s].label;
            return (
              <button
                key={s}
                onClick={() => setStatusFilter(s)}
                data-testid={`history-status-filter-${s}`}
                className={`px-3 py-1 rounded-full text-[11px] font-medium uppercase tracking-wider border transition ${
                  active
                    ? "bg-[#C5A880] text-black border-[#C5A880]"
                    : "text-white/50 border-white/10 hover:border-white/25"
                }`}
              >
                {label}
              </button>
            );
          })}
        </div>

        {/* Activity audit filters — admin/manager only */}
        {isManagerUp && (
          <div className="grid grid-cols-2 gap-3 mt-4" data-testid="history-activity-filters">
            <div>
              <label className="label-luxe">Activity action</label>
              <select
                className="input-luxe !h-12 cursor-pointer"
                value={activityAction}
                onChange={(e) => setActivityAction(e.target.value)}
                data-testid="history-activity-action-filter"
              >
                {ACTION_FILTERS.map((a) => (
                  <option key={a.value} value={a.value} style={{ color: "#111", background: "#fff" }}>
                    {a.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="label-luxe">Team member</label>
              <select
                className="input-luxe !h-12 cursor-pointer"
                value={activityActor}
                onChange={(e) => setActivityActor(e.target.value)}
                data-testid="history-activity-actor-filter"
              >
                <option value="all" style={{ color: "#111", background: "#fff" }}>All members</option>
                {actors.map((a) => (
                  <option key={a.id} value={a.id} style={{ color: "#111", background: "#fff" }}>
                    {a.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}
      </div>

      {loading && <div className="text-white/40 text-sm">Loading...</div>}
      {!loading && filtered.length === 0 && items.length === 0 && (
        <div className="card-luxe text-center py-12 text-white/40">
          <FileText size={32} className="mx-auto mb-3 opacity-50" />
          No quotations yet. <Link to="/new" className="text-[#C5A880] underline">Create one</Link>.
        </div>
      )}
      {!loading && filtered.length === 0 && items.length > 0 && (
        <div className="card-luxe text-center py-12 text-white/40 text-sm">
          No results for your search.
        </div>
      )}

      <div className="space-y-3">
        {filtered.map((q) => (
          <HistoryCard
            key={q.id}
            q={q}
            isManagerUp={isManagerUp}
            can={can}
            onStatusChange={onStatusChange}
            onDuplicate={onDuplicate}
            duplicatingId={duplicatingId}
            onDelete={onDelete}
            activityFor={activityFor}
            setActivityFor={setActivityFor}
          />
        ))}
      </div>
    </div>
    </>
  );
}
